import { create } from 'zustand'
import { getEvents, getEvent, createEvent, updateEvent, updateEventWithTiers, publishEvent, cancelEvent as cancelEventQuery, deleteEvent as deleteEventQuery } from '@unvld/db'
import type { Event, PriceTier } from '@unvld/db'

interface EventsState {
  events: (Event & { price_tiers: PriceTier[] })[]
  currentEvent: (Event & { price_tiers: PriceTier[] }) | null
  total: number
  loading: boolean
  error: string | null

  fetchEvents: (filters?: any) => Promise<void>
  fetchEvent: (id: string) => Promise<void>
  createEvent: (event: Partial<Event> & { price_tiers?: Partial<PriceTier>[] }) => Promise<Event>
  updateEvent: (id: string, updates: Partial<Event>) => Promise<void>
  updateEventWithTiers: (id: string, updates: Partial<Event>, priceTiers?: Partial<PriceTier>[]) => Promise<Event & { price_tiers: PriceTier[] }>
  publishEvent: (id: string) => Promise<void>
  cancelEvent: (id: string) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
}

export const useEvents = create<EventsState>((set) => ({
  events: [],
  currentEvent: null,
  total: 0,
  loading: false,
  error: null,

  fetchEvents: async (filters) => {
    set({ loading: true, error: null })
    try {
      const { events, total } = await getEvents(filters)
      set({ events, total, loading: false })
    } catch (err: any) {
      set({ loading: false, error: err.message })
    }
  },

  fetchEvent: async (id) => {
    set({ loading: true, error: null })
    try {
      const event = await getEvent(id)
      set({ currentEvent: event, loading: false })
    } catch (err: any) {
      set({ loading: false, error: err.message })
    }
  },

  createEvent: async (eventData) => {
    set({ loading: true, error: null })
    try {
      const event = await createEvent(eventData)
      set((state) => ({
        events: [event, ...state.events],
        loading: false,
      }))
      return event
    } catch (err: any) {
      set({ loading: false, error: err.message })
      throw err
    }
  },

  updateEvent: async (id, updates) => {
    try {
      const updated = await updateEvent(id, updates)
      set((state) => ({
        events: state.events.map((e) => (e.id === id ? { ...e, ...updated } : e)),
        currentEvent: state.currentEvent?.id === id ? { ...state.currentEvent, ...updated } : state.currentEvent,
      }))
    } catch (err: any) {
      set({ error: err.message })
    }
  },

  updateEventWithTiers: async (id, updates, priceTiers) => {
    set({ loading: true, error: null })
    try {
      const updated = await updateEventWithTiers(id, updates, priceTiers)
      set((state) => ({
        events: state.events.map((e) => (e.id === id ? updated : e)),
        currentEvent: state.currentEvent?.id === id ? updated : state.currentEvent,
        loading: false,
      }))
      return updated
    } catch (err: any) {
      set({ loading: false, error: err.message })
      throw err
    }
  },

  publishEvent: async (id) => {
    try {
      await publishEvent(id)
      set((state) => ({
        events: state.events.map((e) =>
          e.id === id ? { ...e, status: 'published' as const, published_at: new Date().toISOString() } : e
        ),
      }))
    } catch (err: any) {
      set({ error: err.message })
    }
  },

  cancelEvent: async (id) => {
    try {
      await cancelEventQuery(id)
      set((state) => ({
        events: state.events.map((e) =>
          e.id === id ? { ...e, status: 'cancelled' as const } : e
        ),
        currentEvent: state.currentEvent?.id === id
          ? { ...state.currentEvent, status: 'cancelled' as const }
          : state.currentEvent,
      }))
    } catch (err: any) {
      set({ error: err.message })
      throw err
    }
  },

  deleteEvent: async (id) => {
    try {
      await deleteEventQuery(id)
      set((state) => ({
        events: state.events.filter((e) => e.id !== id),
        currentEvent: state.currentEvent?.id === id ? null : state.currentEvent,
      }))
    } catch (err: any) {
      set({ error: err.message })
      throw err
    }
  },
}))
